// The tenancy page's arrears banner: shows only when the tenancy has rent past due, with the
// same actions as the Arrears page (send the reminder due, log a promise, add a note).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock } from 'lucide-react';
import { Button } from '../ui';
import { useCan } from '../../hooks/usePermission';
import { formatDate, formatMoney } from '../../lib/viz';
import { arrearsApi, daysColour } from '../../lib/arrears';
import { ArrearsDialogs, type ArrearsDialog } from './ArrearsActions';

export function ArrearsBanner({ tenancyId }: { tenancyId: string }) {
  const qc = useQueryClient();
  const canRead = useCan('arrears.read');
  const canRemind = useCan('arrears.remind');
  const canUpdate = useCan('arrears.update');
  const [dialog, setDialog] = useState<ArrearsDialog>(null);
  const [done, setDone] = useState<string | null>(null);
  const d = useQuery({ queryKey: ['/arrears/detail', tenancyId], queryFn: () => arrearsApi.get(tenancyId), enabled: canRead });
  const r = d.data?.row;
  if (!canRead || !d.data || !r) return null;
  const c = daysColour(r.daysOverdue);
  const status = r.paused ? `promised to pay by ${formatDate(r.promise!.promisedOn)} — reminders paused`
    : r.dueNow ? `${r.nextStepLabel} due now`
      : r.lastStepSent >= 4 ? 'every reminder sent'
        : r.upcoming ? `next: ${d.data.ladder[r.upcoming.step - 1]!.label.toLowerCase()} on ${formatDate(r.upcoming.on)}` : '';
  return (
    <div className="mb-2.5 rounded-xl border px-4 py-3" style={{ borderColor: `color-mix(in srgb, ${c} 35%, var(--color-white))`, background: `color-mix(in srgb, ${c} 5%, var(--color-white))` }}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full" style={{ background: `color-mix(in srgb, ${c} 14%, var(--color-white))`, color: c }}><AlarmClock className="h-4 w-4" /></span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-ink-900">{formatMoney(r.owedMinor, r.currency)} in arrears · <span style={{ color: c }}>{r.daysOverdue} days late</span></div>
            <div className="text-[12px] text-ink-600">
              {r.instalments} month{r.instalments === 1 ? '' : 's'} since {formatDate(r.oldestDueDate)} · {r.lastStepLabel ? `${r.lastStepLabel} sent ${formatDate(r.lastReminderAt!)}` : 'nothing sent yet'}{status ? ` · ${status}` : ''}
              {r.replied ? ' · they have replied' : ''}
            </div>
            {done && <div className="mt-1 text-[12px] font-medium text-emerald-700">{done}</div>}
          </div>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          {canRemind && <Button size="sm" icon="Send" disabled={!r.reachedStep || r.lastStepSent >= r.reachedStep} onClick={() => setDialog({ kind: 'remind' })}>{r.nextStepDue ? `Send ${r.nextStepLabel!.toLowerCase()}` : 'Send reminder'}</Button>}
          {canUpdate && <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: 'promise' })}>Log promise</Button>}
          {canUpdate && <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: 'note' })}>Add note</Button>}
          <Link to={`/app/arrears/${tenancyId}`} className="inline-flex items-center px-1 text-[12px] font-medium text-bronze-600 hover:underline">History</Link>
        </div>
      </div>
      <ArrearsDialogs detail={d.data} dialog={dialog} onClose={() => setDialog(null)} onDone={(msg) => {
        setDialog(null); setDone(msg); void d.refetch(); void qc.invalidateQueries({ queryKey: ['/arrears'] }); void qc.invalidateQueries({ queryKey: ['/arrears/summary'] });
      }} />
    </div>
  );
}
